/**
 * @module search.service
 * This module provides search functionality for the application
 * It uses RTK Query to manage search state and API requests
 *
 * @see {@link src/services/apiService/aventureApi.ts} For the base API configuration
 * @see {@link src/services/apiService/axiosService.ts} For the axios instance used for requests
 * @see {@link src/store/search/search.slice.ts} For the Redux slice that manages search state
 */
import { aventureApi } from "@/services/apiService/aventureApi";
import { axiosService, axiosPublicService } from "@/services/apiService/axiosService";
import { searchActions } from "@/store/search/search.slice";

/**
 * Maximum number of retries for search requests
 */
const MAX_RETRIES = 2;

/**
 * Retry delay in milliseconds (exponential backoff)
 */
const getRetryDelay = (attempt: number) => Math.pow(2, attempt) * 300;

/**
 * Helper to determine if we should use the local development proxy
 * In development, we use our local proxy to avoid CORS issues
 */
const useLocalProxy = process.env.NODE_ENV === 'development';

/**
 * Generate the appropriate URL for search requests
 * @param search - The search query
 * @returns The full URL path for the search request
 */
function getSearchUrl(search: string) {
  if (useLocalProxy) {
    // Use the local proxy path in development - fixed to avoid conflating with Next.js API routes
    return `/api-proxy/res/search/${search}`;
  } else {
    // Use the direct API path in production
    return `/res/search/${search}`;
  }
}

/**
 * Extended API slice for search functionality
 * Injects search-specific endpoints into the base aventureApi
 *
 * @constant {import("@reduxjs/toolkit/query").Api} searchApi
 */
const searchApi = aventureApi.injectEndpoints({
  endpoints: (builder) => ({
    /**
     * Query endpoint for retrieving search results
     * Dispatches actions to update the search state in Redux
     */
    getSearchData: builder.query({
      async queryFn(queryParams, { dispatch }) {
        // Early logging to verify function is being called
        console.log("🔍 SEARCH FUNCTION CALLED:", { 
          queryParams,
          time: new Date().toISOString(),
          apiUrl: process.env.NEXT_PUBLIC_API_URL
        });
        
        const { search, action } = queryParams;
        let attempt = 0;
        
        while (attempt <= MAX_RETRIES) {
          try {
            // Reset search state before each attempt
            dispatch(searchActions.resetSearch());
            
            // Log the search request for debugging (development mode only)
            if (process.env.NODE_ENV === 'development') {
              console.log(`[Search Service] Attempt ${attempt+1}/${MAX_RETRIES+1} for search: "${search}"`);
              const url = getSearchUrl(search);
              console.log(`[Search Service] Using URL: ${url}`);
            }
            
            // Always use axiosPublicService for search requests
            // The Next.js rewrites configuration will handle proxying in development
            const { data } = await axiosPublicService.get(getSearchUrl(search), {});
            
            // Log successful response in development mode
            if (process.env.NODE_ENV === 'development') {
              console.log(`[Search Service] Search request successful:`, data);
            }
            
            if (data?.success) {
              dispatch(
                searchActions.setResultCompanies(
                  data?.res?.filter(
                    (el: { typeRecord?: string }) => el?.typeRecord === "Company"
                  )
                )
              );
              dispatch(searchActions.setResultPersons(data?.person));
              dispatch(
                searchActions.setResultFunds([
                  ...(data?.res?.filter(
                    (el: { typeRecord?: string }) => el?.typeRecord !== "Company"
                  ) ?? []),
                  ...(data?.fund ?? []),
                ])
              );
              dispatch(searchActions.setResultTags(data?.tags));
              if (action) action();
            }
            return { data };
          } catch (err) {
            // If this is not the last attempt, try again after delay
            if (attempt < MAX_RETRIES) {
              const delay = getRetryDelay(attempt);
              console.log(`[Search Service] Attempt ${attempt+1} failed, retrying in ${delay}ms...`);
              await new Promise(resolve => setTimeout(resolve, delay));
              attempt++;
            } else {
              // Enhanced error logging for development mode
              console.error(`[Search Service] Error during search request for "${search}" after ${MAX_RETRIES+1} attempts:`, err);
              
              // Log additional details about the error
              if (err && typeof err === 'object') {
                // Use type assertion for Axios error
                const axiosError = err as any;
                
                console.error('[Search Service] Error Details:', {
                  message: axiosError.message,
                  code: axiosError.code,
                  config: axiosError.config ? {
                    url: axiosError.config.url,
                    method: axiosError.config.method,
                    baseURL: axiosError.config.baseURL,
                    headers: axiosError.config.headers,
                  } : 'No config',
                  response: axiosError.response ? {
                    status: axiosError.response.status,
                    statusText: axiosError.response.statusText,
                    data: axiosError.response.data,
                  } : 'No response',
                  request: axiosError.request ? 'Request exists' : 'No request object',
                });
              }
              
              // Create a proper FetchBaseQueryError object
              const errorMessage =
                typeof err === "object" && err !== null && "message" in err
                  ? (err as Error).message
                  : String(err);

              return {
                error: {
                  status: "CUSTOM_ERROR",
                  error: errorMessage,
                },
              };
            }
          }
        }
        
        // This should never be reached due to the while loop and return statements
        return {
          error: {
            status: "CUSTOM_ERROR",
            error: "Unexpected error in retry logic",
          },
        };
      },
      providesTags: ["search"],
    }),
  }),
  overrideExisting: true,
});

/**
 * Lazy query hook for search functionality
 * Use this hook to trigger search requests on demand (e.g., on button click)
 *
 * @example
 * const [triggerSearch, { data, isLoading }] = useLazyGetSearchDataQuery();
 * // Later in code:
 * triggerSearch({ search: 'query', action: () => console.log('Search complete') });
 */
export const { useLazyGetSearchDataQuery } = searchApi;

export default searchApi;
